import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";

export type TooltipSide = "top" | "bottom";
export type TooltipAlign = "center" | "start";

export interface TooltipProps {
  /** Tooltip body — short helper text. */
  content: ReactNode;
  children: ReactNode;
  side?: TooltipSide;
  /** Horizontal anchor. "start" left-aligns the bubble to the trigger, which
   * keeps it on-screen for triggers near the viewport's left edge; the default
   * centers it. */
  align?: TooltipAlign;
  /** Max width of the bubble. */
  className?: string;
}

/* A lightweight tooltip (hover + keyboard focus, no external deps). The bubble
 * is rendered into a portal on <body> and positioned with fixed coordinates, so
 * it is never clipped by an ancestor's `overflow: hidden` (e.g. a rounded KPI
 * card) — it always paints on top of the page. Wrap any focusable trigger. */
export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  className,
}: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;

    const t = trigger.getBoundingClientRect();
    const b = bubble.getBoundingClientRect();
    const gap = 6;

    const top = side === "top" ? t.top - b.height - gap : t.bottom + gap;
    let left = align === "start" ? t.left : t.left + t.width / 2 - b.width / 2;

    // Keep the bubble within the viewport horizontally.
    const margin = 8;
    const maxLeft = window.innerWidth - b.width - margin;
    if (left > maxLeft) left = maxLeft;

    if (left < margin) left = margin;

    setPos({ top, left });
  }, [side, align]);

  // Always keep the bubble mounted (hidden via CSS when closed) so it stays
  // in the accessible tree without requiring hover/focus — matches the prior
  // CSS-only implementation's contract. Position it once mounted, and keep it
  // anchored on scroll/resize while open.
  useLayoutEffect(() => {
    place();
    if (!open) return;

    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  return (
    <span
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {createPortal(
        <span
          ref={bubbleRef}
          role="tooltip"
          style={{ position: "fixed", top: pos.top, left: pos.left }}
          className={cn(
            "pointer-events-none z-[1000] w-max max-w-[260px]",
            "rounded-md border bg-surface px-2.5 py-1.5 text-left text-[11px] leading-snug text-fg-2 shadow-lg",
            open ? "opacity-100" : "invisible opacity-0",
            className,
          )}
        >
          {content}
        </span>,
        document.body,
      )}
    </span>
  );
}
